/**
 * What went wrong when the microphone would not open, as a cause rather than a
 * sentence.
 *
 * This layer is framework-free and cannot translate anything, so it used to
 * hold the English a participant was meant to read — four careful sentences
 * that could never reach a translator, and, as it turned out, never reached a
 * screen either: the only thing rendering them was the `title` of the "not
 * connected" pill, and a microphone failure does not disconnect the room.
 *
 * It names the cause now. The seam says it, and the four causes are the ones
 * the browsers actually distinguish.
 */

/** Why the microphone would not open. */
export type AudioFailure =
  /** The visitor, or their browser, said no. The door is not barred by it. */
  | "denied"
  /** Another application holds the device — usually a call in another tab. */
  | "in-use"
  /** There is no such device to open. */
  | "not-found"
  /** The page's own address cannot reach a microphone at all. */
  | "insecure"
  /** Something else, which is worth saying plainly rather than guessing at. */
  | "unknown";

/**
 * Which of the four a capture error is.
 *
 * Chrome and Firefox report a busy device by name; Safari has reported it as a
 * plain `Error` whose message names the failure to start, which is why the
 * message is read at all.
 */
export function audioFailureFor(error: Error | null): AudioFailure {
  if (!error) return "unknown";

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (
    message.includes("could not start") ||
    message.includes("failed to allocate") ||
    message.includes("hardware error") ||
    name === "notreadableerror" ||
    name === "aborterror"
  ) {
    return "in-use";
  }

  if (name === "notallowederror" || name === "permissiondeniederror") {
    return "denied";
  }

  if (name === "notfounderror" || name === "overconstrainederror") {
    return "not-found";
  }

  if (name === "securityerror") return "insecure";

  return "unknown";
}

/**
 * The catalogue key that says a failure in the visitor's language.
 *
 * A function rather than a map at the call site, so that adding a cause without
 * adding its words fails to compile instead of rendering the key.
 */
export function audioFailureKey(failure: AudioFailure): string {
  return `mic.failure.${failure}`;
}
